import { Observable } from "rxjs";

import { AuthenticationType } from "@vowkey/common/auth/enums/authentication-type";
import { AuthResult } from "@vowkey/common/auth/models/domain/auth-result";
import { TokenTwoFactorRequest } from "@vowkey/common/auth/models/request/identity-token/token-two-factor.request";

import {
  UserApiLoginCredentials,
  PasswordLoginCredentials,
  SsoLoginCredentials,
  AuthRequestLoginCredentials,
  WebAuthnLoginCredentials,
} from "../models/domain/login-credentials";

export abstract class LoginStrategyServiceAbstraction {
  /**
   * The current strategy being used to authenticate.
   * Emits null if the session has timed out.
   */
  abstract currentAuthType$: Observable<AuthenticationType | null>;
  /**
   * If the login strategy uses the email address of the user, this
   * will return it. Otherwise, it will return undefined.
   */
  abstract getEmail(): Promise<string | undefined>;
  /**
   * If the user is logging in with a master password, this will return
   * the master password hash. Otherwise, it will return undefined.
   */
  abstract getMasterPasswordHash(): Promise<string | undefined>;
  /**
   * If the user is logging in with SSO, this will return
   * the email auth token. Otherwise, it will return undefined.
   * @see {@link SsoLoginStrategyData.ssoEmail2FaSessionToken}
   */
  abstract getSsoEmail2FaSessionToken(): Promise<string | undefined>;
  /**
   * Returns the access code if the user is logging in with an
   * Auth Request. Otherwise, it will return undefined.
   */
  abstract getAccessCode(): Promise<string | undefined>;
  /**
   * Returns the auth request ID if the user is logging in with an
   * Auth Request. Otherwise, it will return undefined.
   */
  abstract getAuthRequestId(): Promise<string | undefined>;
  /**
   * Sends a token request to the server using the provided credentials.
   */
  abstract logIn(
    credentials:
      | UserApiLoginCredentials
      | PasswordLoginCredentials
      | SsoLoginCredentials
      | AuthRequestLoginCredentials
      | WebAuthnLoginCredentials,
  ): Promise<AuthResult>;
  /**
   * Sends a token request to the server with the provided two factor token.
   * This uses data stored from {@link LoginStrategyServiceAbstraction.logIn}, so that must be called first.
   * Returns an error if no session data is found.
   */
  abstract logInTwoFactor(twoFactor: TokenTwoFactorRequest): Promise<AuthResult>;
  /**
   * Sends a token request to the server with the provided device verification OTP.
   */
  abstract logInNewDeviceVerification(deviceVerificationOtp: string): Promise<AuthResult>;
}
